import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, Users, Trophy, Clock, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import useInView from '../../hooks/useInView';
import { fetchSerieCours } from '../../api/fff';

dayjs.locale('fr');

const StatsEquipeN2Section = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs pour les animations au scroll
  const [headerRef, headerInView] = useInView({ threshold: 0.2 });
  const [statsGridRef, statsGridInView] = useInView({ threshold: 0.2 });
  const [detailsRef, detailsInView] = useInView({ threshold: 0.2 });

  useEffect(() => {
    const fetchStatsN2 = async () => {
      try {
        setLoading(true);

        // Récupérer le classement N2 Poule B (2025/2026)
        const classementRes = await axios.get(
          "/api/compets/439451/phases/1/poules/2/classement_journees"
        );

        const classementData = classementRes.data;

        // Trouver FEIGNIES/AULNOYE EFC dans le classement
        const efafcStats = Array.isArray(classementData)
          ? classementData.find(team =>
            team.equipe?.short_name_federation?.includes("FEIGNIES") ||
            team.equipe?.short_name_federation?.includes("AULNOYE")
          )
          : classementData["hydra:member"]?.find(team =>
            team.equipe?.short_name_federation?.includes("FEIGNIES") ||
            team.equipe?.short_name_federation?.includes("AULNOYE")
          );

        // 🆕 NOUVELLE APPROCHE : Récupérer la forme récente depuis l'API FFF
        const formeRecenteDetails = await fetchSerieCours();
        const formeRecente = formeRecenteDetails.map(m => m.resultat);

        setStats({
          classement: efafcStats,
          formeRecente,
          formeRecenteDetails,
          dernierMatch: formeRecenteDetails[0] || null
        });
      } catch (err) {
        console.error("Erreur lors du chargement des stats N2:", err);
        setError("Impossible de charger les statistiques");
      } finally {
        setLoading(false);
      }
    };

    fetchStatsN2();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-[#001B56] via-[#002B76] to-[#001B56]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFE400] mx-auto"></div>
            <p className="text-white mt-4">Chargement des statistiques...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || !stats) {
    return (
      <section className="py-16 bg-gradient-to-br from-[#001B56] via-[#002B76] to-[#001B56]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-[#FFE400]">Statistiques temporairement indisponibles</p>
          </div>
        </div>
      </section>
    );
  }

  const { classement, formeRecente, formeRecenteDetails } = stats;

  // Utiliser les données directement depuis l'API classement
  const matchsJoues = classement?.total_games_count || 0;
  const butsMarques = classement?.goals_for_count || 0;
  const butsEncaisses = classement?.goals_against_count || 0;
  const goalAverage = classement?.goals_diff || 0;

  return (
    <section className="py-16 relative overflow-hidden">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          ref={headerRef}
          className="text-center mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-title font-extrabold text-white mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={headerInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.8, delay: headerInView ? 0.2 : 0 }}
          >
            STATISTIQUES
            <motion.span
              className="block text-[#FFE400]"
              initial={{ opacity: 0, x: -50 }}
              animate={headerInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.8, delay: headerInView ? 0.4 : 0 }}
            >
              ÉQUIPE PREMIÈRE
            </motion.span>
          </motion.h2>
          <motion.div
            className="w-24 h-1 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] mx-auto rounded-full"
            initial={{ scaleX: 0 }}
            animate={headerInView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 0.8, delay: headerInView ? 0.6 : 0 }}
          ></motion.div>
          <motion.p
            className="text-[#FFE400]/80 mt-4 font-accent text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: headerInView ? 0.8 : 0 }}
          >
            National 2 - Poule B • Saison 2025/2026
          </motion.p>
        </motion.div>

        {/* Grille de statistiques */}
        <motion.div
          ref={statsGridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          initial={{ opacity: 0, y: 100 }}
          animate={statsGridInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 100 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Position au classement */}
          <motion.div
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#FFE400]/20 hover:border-[#FFE400]/40 transition-all duration-300 transform hover:-translate-y-1"
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={statsGridInView ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0, scale: 0.8, y: 50 }}
            transition={{ duration: 0.6, delay: statsGridInView ? 0.4 : 0 }}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <div className="flex items-center justify-between mb-4">
              <Trophy className="text-[#FFE400] w-8 h-8" />
              <span className="text-3xl font-title font-bold text-[#FFE400]">
                {classement?.rank || 'N/A'}
              </span>
            </div>
            <h3 className="text-white font-accent font-bold text-lg mb-1">Position</h3>
            <p className="text-[#FFE400]/70 text-sm">au classement</p>
          </motion.div>

          {/* Points */}
          <motion.div
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#FFE400]/20 hover:border-[#FFE400]/40 transition-all duration-300 transform hover:-translate-y-1"
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={statsGridInView ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0, scale: 0.8, y: 50 }}
            transition={{ duration: 0.6, delay: statsGridInView ? 0.5 : 0 }}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <div className="flex items-center justify-between mb-4">
              <Target className="text-[#BDFF00] w-8 h-8" />
              <span className="text-3xl font-title font-bold text-[#BDFF00]">
                {classement?.point_count || 0}
              </span>
            </div>
            <h3 className="text-white font-accent font-bold text-lg mb-1">Points</h3>
            <p className="text-[#FFE400]/70 text-sm">au total</p>
          </motion.div>

          {/* Matchs joués */}
          <motion.div
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#FFE400]/20 hover:border-[#FFE400]/40 transition-all duration-300 transform hover:-translate-y-1"
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={statsGridInView ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0, scale: 0.8, y: 50 }}
            transition={{ duration: 0.6, delay: statsGridInView ? 0.6 : 0 }}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <div className="flex items-center justify-between mb-4">
              <Clock className="text-[#FFE400] w-8 h-8" />
              <span className="text-3xl font-title font-bold text-[#FFE400]">
                {matchsJoues}
              </span>
            </div>
            <h3 className="text-white font-accent font-bold text-lg mb-1">Matchs</h3>
            <p className="text-[#FFE400]/70 text-sm">disputés</p>
          </motion.div>

          {/* Différence de buts */}
          <motion.div
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#FFE400]/20 hover:border-[#FFE400]/40 transition-all duration-300 transform hover:-translate-y-1"
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={statsGridInView ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0, scale: 0.8, y: 50 }}
            transition={{ duration: 0.6, delay: statsGridInView ? 0.7 : 0 }}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className={`w-8 h-8 ${goalAverage >= 0 ? 'text-[#BDFF00]' : 'text-red-400'}`} />
              <span className={`text-3xl font-title font-bold ${goalAverage >= 0 ? 'text-[#BDFF00]' : 'text-red-400'}`}>
                {goalAverage > 0 ? '+' : ''}{goalAverage}
              </span>
            </div>
            <h3 className="text-white font-accent font-bold text-lg mb-1">Goal Average</h3>
            <p className="text-[#FFE400]/70 text-sm">{butsMarques} pour / {butsEncaisses} contre</p>
          </motion.div>
        </motion.div>

        {/* Statistiques détaillées */}
        <motion.div
          ref={detailsRef}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          initial={{ opacity: 0, y: 100 }}
          animate={detailsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 100 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {/* Bilan de la saison */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-[#FFE400]/20">
            <h3 className="text-2xl font-title font-bold text-white mb-6 flex items-center">
              <Users className="text-[#FFE400] w-6 h-6 mr-3" />
              Bilan de la saison
            </h3>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-[#FFE400]/80 font-accent">Victoires</span>
                <span className="text-[#BDFF00] font-title font-bold text-xl">
                  {classement?.won_games_count || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#FFE400]/80 font-accent">Nuls</span>
                <span className="text-[#FFE400] font-title font-bold text-xl">
                  {classement?.draw_games_count || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#FFE400]/80 font-accent">Défaites</span>
                <span className="text-red-400 font-title font-bold text-xl">
                  {classement?.lost_games_count || 0}
                </span>
              </div>
            </div>

            {/* Pourcentage de victoires */}
            <div className="mt-6 pt-6 border-t border-[#FFE400]/20">
              <div className="flex justify-between items-center mb-2">
                <span className="text-white font-accent font-semibold">Taux de victoire</span>
                <span className="text-[#BDFF00] font-title font-bold">
                  {matchsJoues > 0 ? Math.round(((classement?.won_games_count || 0) / matchsJoues) * 100) : 0}%
                </span>
              </div>
              <div className="w-full bg-[#001B56] rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${matchsJoues > 0 ? ((classement?.won_games_count || 0) / matchsJoues) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Forme récente */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-[#FFE400]/20">
            <h3 className="text-2xl font-title font-bold text-white mb-6 flex items-center">
              <TrendingUp className="text-[#FFE400] w-6 h-6 mr-3" />
              Forme récente
            </h3>

            {formeRecenteDetails && formeRecenteDetails.length > 0 ? (
              <>
                <div className="flex justify-center gap-4 mb-6">
                  {formeRecenteDetails.map((match, index) => (
                    <div key={`forme-${index}`} className="flex flex-col items-center group relative">
                      {/* Cercle V/N/D */}
                      <div
                        className={`w-14 h-14 rounded-full flex items-center justify-center font-title font-bold text-xl transition-transform group-hover:scale-110 ${match.resultat === 'V'
                          ? 'bg-[#BDFF00] text-[#001B56]'
                          : match.resultat === 'N'
                            ? 'bg-[#FFE400] text-[#001B56]'
                            : 'bg-red-500 text-white'
                          }`}
                      >
                        {match.resultat}
                      </div>

                      {/* Détails au survol (tooltip) */}
                      <div className="absolute top-full mt-2 opacity-0 group-hover:opacity-100 transition-opacity bg-[#001B56] border border-[#FFE400]/30 rounded-lg p-3 min-w-[200px] z-10 pointer-events-none">
                        <p className="text-[#FFE400] text-xs font-accent font-bold mb-1">
                          {match.adversaire}
                        </p>
                        <p className="text-white text-sm font-title font-bold mb-1">
                          {match.score} <span className="text-[#FFE400]/60">({match.domicile})</span>
                        </p>
                        <p className="text-[#FFE400]/60 text-xs font-accent">
                          {dayjs(match.date).format('DD MMM YYYY')}
                        </p>
                        <p className="text-[#BDFF00] text-xs font-accent mt-1">
                          {match.competition}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <p className="text-[#FFE400]/80 text-sm font-accent">
                    V = Victoire • N = Nul • D = Défaite
                  </p>
                  <p className="text-white/60 text-xs mt-2 font-accent">
                    Derniers {formeRecenteDetails.length} matchs disputés • Survolez pour les détails
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-[#FFE400]/70 font-accent">Aucun match récent</p>
              </div>
            )}

            {/* Statistiques offensives/défensives */}
            <div className="mt-6 pt-6 border-t border-[#FFE400]/20">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-title font-bold text-[#BDFF00]">
                    {matchsJoues > 0 ? (butsMarques / matchsJoues).toFixed(1) : '0.0'}
                  </div>
                  <div className="text-[#FFE400]/80 text-sm font-accent">Buts/match</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-title font-bold text-red-400">
                    {matchsJoues > 0 ? (butsEncaisses / matchsJoues).toFixed(1) : '0.0'}
                  </div>
                  <div className="text-[#FFE400]/80 text-sm font-accent">Encaissés/match</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default StatsEquipeN2Section;